/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { WHACK_LEVELS, BUBBLE_COLORS, shuffleArray, playPopSound, playWhackCorrect, playWhackWrong, playComboSound, playTimeUpSound, playGameOverSound, speakWhack } from "@/utils/whackGameData";
import { Home, Star, Volume2, VolumeX, Trophy, RotateCcw, Zap, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhackGame = ({ startLevel = null, onBack }) => {
  const [phase, setPhase] = useState("playing"); // playing, results
  const [levels, setLevels] = useState([]);
  const [levelIdx, setLevelIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [moles, setMoles] = useState(Array(9).fill(null));
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10);
  const [soundOn, setSoundOn] = useState(true);
  const [hitFeedback, setHitFeedback] = useState(null);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [saveName, setSaveName] = useState(() => {
    try { const d = localStorage.getItem('alba_game_players'); if (d) { const p = JSON.parse(d); return p[0] || ""; } } catch (err) { logger.debug("game op failed", err); } return "";
  });
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const spawnRef = useRef(null);
  const timerRef = useRef(null);
  const lockRef = useRef(false);
  const { width, height } = useWindowSize();

  // Setup levels
  useEffect(() => {
    if (startLevel !== null) {
      const lvl = WHACK_LEVELS.find(l => l.id === startLevel);
      setLevels(lvl ? [lvl] : WHACK_LEVELS);
    } else {
      setLevels([...WHACK_LEVELS]);
    }
  }, [startLevel]);

  const currentLevel = levels[levelIdx];
  const currentQ = currentLevel?.questions[qIdx];

  // Spawn moles
  const spawnMoles = useCallback(() => {
    if (!currentQ || !currentLevel) return;
    const pool = [...currentQ.pool];
    const correct = currentQ.correct;

    // Pick 3-5 random positions
    const positions = shuffleArray([0,1,2,3,4,5,6,7,8]);
    const count = Math.min(3 + Math.floor(Math.random() * 3), 5);
    const active = positions.slice(0, count);

    // Ensure correct answer is in one of them
    const correctPos = active[Math.floor(Math.random() * active.length)];

    const newMoles = Array(9).fill(null);
    active.forEach(pos => {
      if (pos === correctPos) {
        newMoles[pos] = {
          value: correct,
          isCorrect: true,
          color: BUBBLE_COLORS[Math.floor(Math.random() * BUBBLE_COLORS.length)],
          id: Date.now() + pos,
        };
      } else {
        // Pick a random wrong answer
        const wrongs = pool.filter(p => p !== correct);
        const wrong = wrongs[Math.floor(Math.random() * wrongs.length)];
        newMoles[pos] = {
          value: wrong,
          isCorrect: false,
          color: BUBBLE_COLORS[Math.floor(Math.random() * BUBBLE_COLORS.length)],
          id: Date.now() + pos,
        };
      }
    });

    if (soundOn) playPopSound();
    setMoles(newMoles);

    // Hide after moleStay
    setTimeout(() => {
      setMoles(prev => prev.map((m, i) => {
        if (active.includes(i) && m?.id === newMoles[i]?.id) return null;
        return m;
      }));
    }, currentLevel.moleStay);
  }, [currentQ, currentLevel, soundOn]);

  // Start question
  const startQuestion = useCallback(() => {
    if (!currentLevel || !currentQ) return;
    lockRef.current = false;
    setMoles(Array(9).fill(null));
    setTimeLeft(currentLevel.timePerQuestion);
    setHitFeedback(null);

    if (soundOn) setTimeout(() => speakWhack(currentQ.q), 200);

    // Start spawn interval
    clearInterval(spawnRef.current);
    spawnRef.current = setInterval(() => {
      if (!lockRef.current) spawnMoles();
    }, currentLevel.spawnInterval);
    spawnMoles();

    // Start timer
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearInterval(spawnRef.current);
          if (soundOn) playTimeUpSound();
          setHitFeedback({ type: 'timeout', text: `Waktu habis! Jawaban: ${currentQ?.correct || '?'}` });
          lockRef.current = true;
          setCombo(0);
          setTotalAnswered(p => p + 1);
          setTimeout(() => nextQuestion(), 2500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [currentLevel, currentQ, soundOn, spawnMoles]);

  useEffect(() => {
    if (phase === 'playing' && currentQ) {
      startQuestion();
    }
    return () => {
      clearInterval(spawnRef.current);
      clearInterval(timerRef.current);
    };
  }, [phase, levelIdx, qIdx, levels]);

  const nextQuestion = useCallback(() => {
    setHitFeedback(null);
    const nextQ = qIdx + 1;
    if (nextQ < (currentLevel?.questions.length || 0)) {
      setQIdx(nextQ);
    } else {
      // Level complete - check badge
      if (currentLevel && correctCount >= Math.floor(currentLevel.questions.length * 0.6)) {
        setEarnedBadges(prev => prev.includes(currentLevel.badge) ? prev : [...prev, currentLevel.badge]);
      }
      const nextLvl = levelIdx + 1;
      if (nextLvl < levels.length) {
        setLevelIdx(nextLvl);
        setQIdx(0);
      } else {
        // Game over
        clearInterval(spawnRef.current);
        clearInterval(timerRef.current);
        if (soundOn) playGameOverSound();
        setPhase('results');
        recordGameWon("whack");
      }
    }
  }, [qIdx, levelIdx, levels, currentLevel, correctCount, soundOn]);

  const handleWhack = (index) => {
    if (lockRef.current) return;
    const mole = moles[index];
    if (!mole) return;

    if (mole.isCorrect) {
      lockRef.current = true;
      clearInterval(spawnRef.current);
      clearInterval(timerRef.current);

      const newCombo = combo + 1;
      setCombo(newCombo);
      if (newCombo > maxCombo) setMaxCombo(newCombo);

      const comboBonus = newCombo >= 3 ? newCombo * 10 : 0;
      const points = 100 + comboBonus;
      setScore(s => s + points);
      setCorrectCount(c => c + 1);
      setTotalAnswered(p => p + 1);

      if (soundOn) {
        playWhackCorrect();
        if (newCombo >= 3) setTimeout(() => playComboSound(), 200);
        setTimeout(() => speakWhack(newCombo >= 3 ? "Luar biasa!" : "Yeay!"), 150);
      }

      setHitFeedback({
        type: 'correct',
        text: newCombo >= 5 ? `LUAR BIASA! x${newCombo}` : newCombo >= 3 ? `COMBO x${newCombo}!` : 'Yeay!',
        points,
      });
      setMoles(Array(9).fill(null));

      setTimeout(() => nextQuestion(), 1000);
    } else {
      if (soundOn) { playWhackWrong(); setTimeout(() => speakWhack("Ups!"), 100); }
      setCombo(0);
      setMoles(prev => prev.map((m, i) => i === index ? null : m));
      setHitFeedback({ type: 'wrong', text: `Ups! Jawaban yang benar: ${currentQ.correct}` });
      setTimeout(() => setHitFeedback(null), 2500);
    }
  };

  const restartGame = () => {
    setLevelIdx(0);
    setQIdx(0);
    setScore(0);
    setCombo(0);
    setMaxCombo(0);
    setCorrectCount(0);
    setTotalAnswered(0);
    setEarnedBadges([]);
    setMoles(Array(9).fill(null));
    setPhase('playing');
    lockRef.current = false;
    setSaveName(() => {
      try { const d = localStorage.getItem('alba_game_players'); if (d) { const p = JSON.parse(d); return p[0] || ""; } } catch (err) { logger.debug("game op failed", err); } return "";
    });
    setScoreSaved(false);
    setSaving(false);
  };

  if (!currentLevel && phase === 'playing') return null;

  const timerPercent = currentLevel ? (timeLeft / currentLevel.timePerQuestion) * 100 : 100;
  const timerColor = timeLeft <= 3 ? '#FF4444' : timeLeft <= 5 ? '#FFD700' : '#4ade80';

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto px-2" data-testid="whack-game-container">
      {phase === 'results' && <Confetti width={width} height={height} colors={["#FFD700","#FF6B35","#4ECDC4","#FF6B6B"]} recycle={false} numberOfPieces={300} />}

      {phase === 'playing' && currentLevel && (
        <>
          {/* Top Bar */}
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full mb-3">
            <div className="glass-card-strong rounded-xl p-3 border border-gold/20">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{currentLevel.icon}</span>
                  <span className="font-fredoka text-sm" style={{ color: currentLevel.color }}>
                    Lv{currentLevel.id} {currentLevel.name}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-white fill-gold" />
                    <span className="font-fredoka text-white text-sm">{score}</span>
                  </div>
                  {combo >= 2 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.2, 1] }}
                      className="font-fredoka text-xs px-2 py-0.5 rounded-full"
                      style={{ color: currentLevel.color, border: `1px solid ${currentLevel.color}`, background: `${currentLevel.color}15` }}
                    >
                      x{combo}
                    </motion.span>
                  )}
                  <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="whack-sound-toggle">
                    {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                  </Button>
                  <Button onClick={onBack} variant="outline" size="sm" className="border-gold/20 text-pink-300 hover:bg-gold/10 h-8 px-2" data-testid="whack-back-btn">
                    <Home className="w-3.5 h-3.5 mr-1" />Menu
                  </Button>
                </div>
              </div>

              {/* Question + Timer */}
              <div className="flex items-center gap-3">
                {/* Circular Timer */}
                <div className="relative w-12 h-12 flex-shrink-0">
                  <svg className="w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,215,0,0.15)" strokeWidth="4" />
                    <circle cx="24" cy="24" r="20" fill="none" stroke={timerColor} strokeWidth="4"
                      strokeDasharray={`${2 * Math.PI * 20}`}
                      strokeDashoffset={`${2 * Math.PI * 20 * (1 - timerPercent / 100)}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 0.5s linear, stroke 0.3s' }}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center font-fredoka text-sm font-bold" style={{ color: timerColor }}>
                    {timeLeft}
                  </span>
                </div>
                <div className="flex-1 text-center rounded-lg p-2 border" style={{ borderColor: `${currentLevel.color}33`, background: `${currentLevel.color}10` }}>
                  <p className="font-fredoka text-base sm:text-lg font-bold" style={{ color: currentLevel.color }} data-testid="whack-question">
                    {currentQ?.q}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feedback */}
          <AnimatePresence>
            {hitFeedback && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className={`mb-2 px-5 py-1.5 rounded-full font-fredoka text-sm font-bold ${
                  hitFeedback.type === 'correct' ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                  : hitFeedback.type === 'timeout' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                  : 'bg-red-500/20 text-red-400 border border-red-500/40'
                }`}
                data-testid="whack-feedback"
              >
                {hitFeedback.text}
                {hitFeedback.points && <span className="ml-2 text-white">+{hitFeedback.points}</span>}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mole Grid */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="rounded-xl p-4 sm:p-6 border-2 w-full"
            style={{
              background: 'rgba(30,20,10,0.85)',
              borderColor: `${currentLevel.color}33`,
              boxShadow: `0 0 30px ${currentLevel.color}15`,
            }}
          >
            <div className="grid grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-lg mx-auto">
              {moles.map((mole, idx) => (
                <div
                  key={idx}
                  data-testid={`whack-hole-${idx}`}
                  className="relative flex items-center justify-center cursor-pointer"
                  style={{ aspectRatio: '1' }}
                  onClick={() => handleWhack(idx)}
                >
                  {/* Hole base */}
                  <div className="absolute inset-x-2 bottom-0 h-4 rounded-b-full"
                    style={{ background: 'radial-gradient(ellipse, #1a0f00 60%, transparent 100%)' }}
                  />
                  <div className="w-full h-full rounded-2xl border-2 border-gold/20 glass-card/60 flex items-center justify-center overflow-hidden relative"
                    style={{ boxShadow: 'inset 0 4px 12px rgba(0,0,0,0.4)' }}
                  >
                    <AnimatePresence>
                      {mole && (
                        <motion.div
                          key={mole.id}
                          initial={{ scale: 0, y: 30 }}
                          animate={{ scale: 1, y: 0 }}
                          exit={{ scale: 0, y: 20 }}
                          transition={{ type: "spring", stiffness: 400, damping: 15 }}
                          className="absolute inset-1 rounded-xl flex items-center justify-center cursor-pointer select-none active:scale-90 transition-transform"
                          style={{
                            background: `radial-gradient(circle at 35% 35%, ${mole.color}DD, ${mole.color}99)`,
                            boxShadow: `0 4px 15px ${mole.color}50, inset 0 -3px 6px rgba(0,0,0,0.2)`,
                            border: `2px solid ${mole.color}`,
                          }}
                          data-testid={`whack-mole-${idx}`}
                        >
                          {/* Shine */}
                          <div className="absolute top-1.5 left-2 w-4 h-2 rounded-full bg-white/25" />
                          <span className="font-fredoka font-bold text-gold text-2xl sm:text-3xl drop-shadow-md"
                            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                          >
                            {mole.value}
                          </span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Question progress */}
          <div className="mt-3 flex gap-1 justify-center">
            {currentLevel.questions.map((_, i) => (
              <div key={i} className="w-2.5 h-2.5 rounded-full transition-colors"
                style={{ background: i < qIdx ? '#4ade80' : i === qIdx ? currentLevel.color : '#3a2a1a' }}
              />
            ))}
          </div>
        </>
      )}

      {/* Results Screen */}
      {phase === 'results' && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md mx-auto"
        >
          <div className="glass-card-strong rounded-2xl p-6 sm:p-8 border-2 border-gold/20/20 text-center"
            style={{ boxShadow: '0 0 40px rgba(255,215,0,0.15)' }}
          >
            <Trophy className="w-14 h-14 mx-auto mb-3 text-white" />
            <h2 className="font-fredoka text-2xl sm:text-3xl font-bold text-white mb-1">Permainan Selesai!</h2>
            <p className="font-fredoka text-gold-light/80 text-sm mb-5">Hasil petualanganmu</p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                <p className="font-fredoka text-gold-light/80 text-xs">Skor</p>
                <p className="font-fredoka text-2xl text-gold font-bold">{score}</p>
              </div>
              <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                <p className="font-fredoka text-gold-light/80 text-xs">Benar</p>
                <p className="font-fredoka text-2xl text-green-400 font-bold">{correctCount}/{totalAnswered}</p>
              </div>
              <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                <p className="font-fredoka text-gold-light/80 text-xs">Max Combo</p>
                <p className="font-fredoka text-2xl font-bold" style={{ color: '#FF69B4' }}>x{maxCombo}</p>
              </div>
              <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                <p className="font-fredoka text-gold-light/80 text-xs">Badge</p>
                <p className="font-fredoka text-lg text-gold font-bold">{earnedBadges.length}</p>
              </div>
            </div>

            {/* Badges */}
            {earnedBadges.length > 0 && (
              <div className="mb-5">
                <p className="font-fredoka text-gold-light/80 text-xs mb-2">Badge Pencapaian</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {earnedBadges.map((badge, i) => (
                    <motion.div
                      key={badge}
                      initial={{ scale: 0, rotate: -20 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: i * 0.15, type: "spring" }}
                      className="px-3 py-1.5 rounded-full border-2 border-gold/20 bg-gradient-to-r from-yellow-400 to-amber-500/10 font-fredoka text-xs font-bold text-white"
                    >
                      <Star className="w-3 h-3 inline mr-1 fill-gold text-white" />{badge}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {!scoreSaved ? (
                <div className="flex gap-2 mb-1">
                  <input
                    data-testid="whack-save-name-input"
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="Nama kamu..."
                    maxLength={50}
                    className="flex-1 glass-card border border-gold/20 rounded-lg px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/20"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && saveName.trim() && !saving) {
                        setSaving(true);
                        axios.post(`${API}/game/record`, {
                          player_name: saveName.trim(),
                          mode: "whack",
                          duration_seconds: 0,
                          score,
                          level: currentLevel?.name || "All",
                          opponent_type: "solo",
                          moves_count: totalAnswered,
                        }).then(() => setScoreSaved(true)).catch(logger.error).finally(() => setSaving(false));
                      }
                    }}
                  />
                  <Button
                    onClick={() => {
                      if (!saveName.trim() || saving) return;
                      setSaving(true);
                      axios.post(`${API}/game/record`, {
                        player_name: saveName.trim(),
                        mode: "whack",
                        duration_seconds: 0,
                        score,
                        level: currentLevel?.name || "All",
                        opponent_type: "solo",
                        moves_count: totalAnswered,
                      }).then(() => setScoreSaved(true)).catch(logger.error).finally(() => setSaving(false));
                    }}
                    disabled={!saveName.trim() || saving}
                    className="font-fredoka font-bold text-brown-dark px-4" style={{ background: '#4ade80' }}
                    data-testid="whack-save-score-btn">
                    <Save className="w-4 h-4 mr-1" />{saving ? '...' : 'Simpan'}
                  </Button>
                </div>
              ) : (
                <p className="text-green-400 font-fredoka text-sm mb-1" data-testid="whack-score-saved">Skor tersimpan!</p>
              )}
              <Button onClick={restartGame} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="whack-replay-btn">
                <RotateCcw className="w-4 h-4 mr-2" />Main Lagi
              </Button>
              <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka" data-testid="whack-finish-btn">
                <Home className="w-4 h-4 mr-2" />Kembali ke Menu
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default WhackGame;
